import React, { useEffect, useState } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import SearchableSelect from '../common/SearchableSelect';
import { useToast } from '../../context/ToastContext';
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { CustodianOption } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';

interface Props {
  value: string | null;
  /** Name of the current custodian, so the box is not blank when they are outside the first page of results. */
  valueLabel?: string | null;
  onChange: (id: string, option: CustodianOption | null) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
  'data-help'?: string;
}

/**
 * F10 custodian picker: members of the selected institution only (names and ids), searched on the server.
 * Uses the fixed-asset custodians endpoint, so a transfer clerk without USER_VIEW can still pick a person.
 */
const CustodianPicker: React.FC<Props> = ({
  value, valueLabel, onChange, label = 'Custodian', helperText, disabled, sx, ...rest
}) => {
  const toast = useToast();
  const [options, setOptions] = useState<CustodianOption[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      FixedAssetTransferService.custodians(text.trim() || undefined)
        .then(r => { if (live) setOptions(Array.isArray(r) ? r : []); })
        .catch(e => { if (live) { setOptions([]); toast.showApiError(e, 'Failed to load custodians'); } })
        .finally(() => { if (live) setLoading(false); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedOption = value && valueLabel ? { id: value, name: valueLabel } : undefined;

  return (
    <SearchableSelect<CustodianOption>
      label={label} options={options} value={value} selectedOption={selectedOption}
      onChange={onChange} getOptionLabel={o => o.name} getOptionId={o => o.id}
      loading={loading} disableClientFilter onInputChange={setText}
      helperText={helperText} disabled={disabled} sx={sx} {...rest}
    />
  );
};

export default CustodianPicker;
